import { useState } from 'react';
import { generatePath } from 'react-router-dom';
import { routes } from '$src/routes';
import { handleBackToRingDashboard } from '$src/utils/handleBack';
import Header from '$src/components/Steps/Header';
import AuthorizeDialog from '$src/components/AuthorizeDialog';
import Collapse from '$src/components/Collapse';
import ContentBox from '$src/components/ContentBox';
import useStores from '$src/hooks/useStores';
import Draggable from '$src/components/Draggable';
import useLocationContext from '$src/hooks/useLocationContext';
import useModalEvents from '$src/hooks/useModalEvents';
import { Link } from '@amzn/ring-react-components/csr/components/Link';
import { CLICK, START_FREE_TRIAL_ACTION_ID, ACCEPT_AUTHORIZATION_ACTION_ID, DENY_AUTHORIZATION_ACTION_ID } from '$src/constants/actions';
import { redirectToOnboardingIntroduction } from '$src/utils/redirect';
import {
  RING_WEB_URL,
  VIDEO_URL,
  FREE_TRIAL_TERMS_URL,
  TERMS_URL,
  RR_TERMS_URL,
  RR_PRIVACY_NOTICE_URL,
  PRIVACY_NOTICE_URL,
} from '$src/constants/urls';
import ReactPlayer from 'react-player';

import { ReferralsIcon, MonitoringStationIcon, HelpCenterIcon, SmartAlertsIcon, TwofaIcon } from '@ring-icons';

import freeTrialHeader from '$src/assets/img/free_trial_header.gif';
import dashboardDisarmed from '$src/assets/img/dashboard_disarmed.png';
import motionSettings from '$src/assets/img/motion_settings.png';
import selectDevices from '$src/assets/img/select_devices.png';
import alertPreferences from '$src/assets/img/alert_preferences.png';
import Footer from './Footer';

import './index.less';

function FreeTrial() {
  const {
    sessionStore: { isMobile, setAuthorizationAccepted, authorizeVSG, featureFlags },
    statsStore: { sendStats },
    locationContextStore: { isFreeTrialActive, isFreeTrialUnavailable, subscribeToFreeTrial, location, locationOwnerId },
  } = useStores();
  const locationId = useLocationContext();
  const [disabled, setDisabled] = useState(false);
  const [toggleModal, showModal] = useModalEvents();
  const overagesThreshold = location?.overagesThreshold ?? 0;

  if (isFreeTrialActive) {
    redirectToOnboardingIntroduction(locationId);
    return null;
  }
  if (isFreeTrialUnavailable) {
    handleBackToRingDashboard(isMobile);
    return null;
  }

  const delay = (ms) =>
    new Promise((resolve) => {
      setTimeout(resolve, ms);
    });

  const onContinue = async () => {
    setDisabled(true);
    setAuthorizationAccepted();
    await subscribeToFreeTrial(locationOwnerId, locationId);
    await sendStats(CLICK, undefined, ACCEPT_AUTHORIZATION_ACTION_ID);
    await delay(5000);
    await authorizeVSG(`${window.location.origin}${generatePath(`${routes.onboardingIntroduction}`, { locationId })}`);
  };

  const onDeny = async () => {
    await sendStats(CLICK, undefined, DENY_AUTHORIZATION_ACTION_ID);
  };

  const onStartTrial = async () => {
    await sendStats(CLICK, undefined, START_FREE_TRIAL_ACTION_ID);

    const onboarding30FeatureFlagName = "stark-feature-next-onboarding-3-0";
    if (featureFlags[onboarding30FeatureFlagName]?.enabled === false) {
      // use old flow
      toggleModal();
    } else {
      // subscribe to free trial directly without showing modal
      setDisabled(true);
      await subscribeToFreeTrial(locationOwnerId, locationId);
      await delay(5000);
      redirectToOnboardingIntroduction(locationId);
      return null;
    }
  };

  return (
    <div className="freetrial__main" data-testid="free-trial">
      <div className="freetrial">
        <Header hideBack handleClose={() => handleBackToRingDashboard(isMobile)}>
          Virtual Security Guard
        </Header>

        <div className="freetrial__header__wrapper">
          <img src={freeTrialHeader} alt="free trial header" className="freetrial__header" />
        </div>

        <div className="freetrial__title">The Highest Level of Security</div>
        <div className="freetrial__subtitle">
          Deter break-ins, trespassing, theft, property damage, and more for your home or business with live professional Guards.
        </div>
        <Draggable rootClass="freetrial__">
          <div className="freetrial__slider-content">
            <ContentBox icon={<ReferralsIcon />}>
              <h1> Free Trial </h1>
              You've been selected for a two-week free trial of Virtual Security Guard, the highest level of security. <b>
                You will not be charged
              </b>{' '}
              at the end of your trial. You can purchase a Virtual Security Guard subscription on
              <Link link={RING_WEB_URL} type="external" className="freetrial__ringweb">
                {' '}
                Ring.com{' '}
              </Link>
              at anytime.
            </ContentBox>
            <ContentBox icon={<MonitoringStationIcon />}>
              <h1>Security Done for You</h1>
              When you arm your Ring Alarm, Virtual Security Guard monitors for people. When a person is detected, Guards from Rapid Response
              immediately live monitor the situation.
            </ContentBox>
            <ContentBox icon={<HelpCenterIcon />}>
              <h1>Proactive Protection</h1>
              Guards provide real-time deterrence using your camera’s speaker and siren, and will immediately contact emergency dispatch for police,
              fire and medical services in the event of an emergency or call you in the event of suspicious activity.
            </ContentBox>
            <ContentBox icon={<SmartAlertsIcon />}>
              <h1>Notifications that Matter</h1>
              Guards will send text messages and/or call you about activity observed on your property. You control what Guards contact you for and how
              they contact you. You can also customize an emergency contact list so others are contacted.
            </ContentBox>
            <ContentBox icon={<TwofaIcon />}>
              <h1>Designed for Privacy</h1>
              You determine which cameras are monitored by Guards and when they are monitored. Guards will only monitor your selected cameras in Home
              or Away mode. You can also set Privacy Zones to prevent Guards from seeing a designated area. When you disarm your system, Guards will
              immediately lose access to your cameras. You are always in control.
            </ContentBox>
          </div>
        </Draggable>

        <div className="freetrial__title freetrial__title--secondary">How Virtual Security Guard Works</div>
        <div className="freetrial__video-wrapper">
          <ReactPlayer className="react-player" url={VIDEO_URL} controls={true} volume={0.5} width="100%" />
        </div>

        <Draggable rootClass="freetrial__">
          <div className="freetrial__slider-content">
            <ContentBox image={dashboardDisarmed}>
              <h1> You’re in Control </h1>
              Guards only monitor your selected cameras when your system is armed to Home or Away and a person is detected. Guards do not have access
              to your Event History, cannot download videos, and cannot access your cameras when your system is disarmed.
            </ContentBox>
            <ContentBox image={motionSettings}>
              <h1>Customize Your Coverage</h1>
              You can adjust your Motion Zones to cover areas within your camera’s view that you want Guards to monitor. You can also use Privacy
              Zones to exclude areas on your property you don’t want Guards to see.
            </ContentBox>
            <ContentBox image={selectDevices}>
              <h1>Cameras You Choose</h1>
              You select the cameras you want monitored, and you can change these settings at any time. If you decide to unenroll a camera from
              monitoring, events from that camera will immediately stop being sent to Guards for review.
            </ContentBox>
            <ContentBox image={alertPreferences}>
              <h1>Personalize Your Alerts</h1>
              Depending on your alert preferences, Guards will send text notifications to you and your emergency contacts about activity observed at
              your property. If Guards request police, fire, or medical dispatch, they will call you and your emergency contacts.
            </ContentBox>
          </div>
        </Draggable>

        <div className="freetrial__title freetrial__title--secondary">Frequently Asked Questions</div>

        <div className="freetrial__faq">
          <Collapse title="Will I be charged when my trial is over?">
            No, you will <b>not</b> be charged when your trial ends — you can purchase a subscription for Virtual Security Guard separately.
          </Collapse>

          <Collapse title="Who will be monitoring my property?">
            When a person is detected, highly trained Guards from Rapid Response, Ring's trusted Alarm Operating Center, will review the event in
            real-time. Guards are based in controlled facilities in the US and undergo extensive response training to protect your home or business.
          </Collapse>

          <Collapse title="How will I know Guards are only monitoring where and when I want?">
            You’re in complete control over which cameras are monitored by Guards and when, and you can update your enrolled cameras at any time.
            Guards only monitor when your system is in Home or Away mode and cannot access your camera's Live View unless motion is detected. For
            added control over your monitoring, you can use Motion Zones to control which areas are monitored by Guards, and Privacy Zones to control
            what areas Guards can’t monitor. You can also Snooze your events while your system is in Home or Away mode, which temporarily stops events
            from being sent to Guards for review, for a duration you choose.
          </Collapse>

          <Collapse title="Why should I try Virtual Security Guard?">
            Virtual Security Guard puts the monitoring of your property in the hands of highly-trained professionals, available 24/7 so your property
            is always protected when you arm your Ring system. Guards can provide proactive deterrence while you’re away or sleeping, send
            notifications about activity they observe, and quickly request dispatch of emergency services without an alarm sensor being triggered.
          </Collapse>

          <Collapse title="What do you mean by 'Faster Emergency Response'?">
            Many jurisdictions prioritize emergency response for visually verified events. This means that when Guards visually verify an emergency at
            your location and request police dispatch, they confirm the event prioritization with the dispatcher, and your event may be categorized by
            the dispatcher as a higher priority request than dispatch requests that are not visually verified.
          </Collapse>

          <Collapse title="How does the free trial work?">
            Your trial period starts immediately after you tap <b>Start Free Trial</b> and <b>Authorize</b> the service on the next page. You must
            complete a few easy steps to activate the service for live Guard monitoring. You will have Virtual Security Guard for free for up to 14
            days (or {overagesThreshold} monitored events, whichever comes first) with no commitments. You can opt out of your trial at any time.
          </Collapse>

          <Collapse title="How do I set up Virtual Security Guard?">
            After tapping <b>Start Free Trial</b>, you will begin setup. The first step is authorizing Virtual Security Guard, which allows Rapid
            Response Guards to view events on the cameras you select for monitoring. Next, you’ll select the cameras you want Guards to monitor,
            confirm your emergency contacts and customize your alert preferences. Setup only takes a couple minutes and must be completed to use the
            service.
          </Collapse>
        </div>

        <div className="freetrial__title freetrial__title--primary">Start your free trial today!</div>

        <p className="freetrial__subtext">
          For additional questions, reach out to our Customer Service team at <a href="tel:1-866-894-8282">1-866-894-8282</a>.
          <br />
          <br />
          By clicking Start Free Trial, you agree to the terms and conditions below. You also agree to{' '}
          <Link link={TERMS_URL} type="external">
            Ring’s Terms of Service
          </Link>{' '}
          and{' '}
          <Link link={RR_TERMS_URL} type="external">
            Rapid Response Monitoring Services, Inc.’s Terms of Service
          </Link>{' '}
          and{' '}
          <Link link={RR_PRIVACY_NOTICE_URL} type="external">
            Rapid Response Monitoring Services, Inc.’s Privacy Policy
          </Link>{' '}
          and{' '}
          <Link link={PRIVACY_NOTICE_URL} type="external">
            Ring’s Privacy Notice
          </Link>{' '}
          .
        </p>

        <Link link={FREE_TRIAL_TERMS_URL} type="external" className="freetrial__terms">
          {'Free Trial Terms and Conditions >'}
        </Link>

        <Footer disabled={disabled} onClick={onStartTrial} />
      </div>
      <AuthorizeDialog
        showModal={showModal}
        handleAuthorize={onContinue}
        handleDeny={onDeny}
        setModalVisible={toggleModal}
        loadingButton={disabled}
      />
    </div>
  );
}

export default FreeTrial;
